import { TypedDocumentNode, gql, useLazyQuery } from '@apollo/client';
import { CSSProperties, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Pagination } from '@sorare/core/src/atoms/navigation/Pagination';
import { ToggleFilter } from '@sorare/core/src/components/live/Filters/ToggleFilter';

import { Ranking } from '../RankRewards/Ranking';
import {
  PaginatedRankingsQuery,
  PaginatedRankingsQueryVariables,
  PaginatedRankings_so5Ranking,
} from './__generated__/index.graphql';

const PAGINATED_RANKINGS_QUERY = gql`
  query PaginatedRankingsQuery(
    $slug: String!
    $page: Int
    $pageSize: Int!
    $onlyFollowed: Boolean
  ) {
    so5 {
      so5Leaderboard(slug: $slug) {
        slug
        so5RankingsPaginated(
          page: $page
          pageSize: $pageSize
          onlyFollowed: $onlyFollowed
        ) {
          currentPage
          totalCount
          so5Rankings {
            id
            so5Lineup {
              id
              so5LeaderboardContender {
                slug
              }
            }
            ...RankRewardsRanking_so5Ranking
          }
        }
      }
    }
  }
  ${Ranking.fragments.so5Ranking}
` as TypedDocumentNode<PaginatedRankingsQuery, PaginatedRankingsQueryVariables>;

const Rankings = styled.div`
  --row-height: calc(6 * var(--unit));
  height: calc(var(--nb-rows) * var(--row-height));
`;

const PAGE_SIZE = 20;

export type Props = {
  slug: string;
  so5Ranking?: PaginatedRankings_so5Ranking;
  onClickRanking?: (contender: string) => void;
};

export const PaginatedRankings = ({
  slug,
  so5Ranking,
  onClickRanking,
}: Props) => {
  const { formatMessage } = useIntl();
  const [page, setPage] = useState<number>();
  const [onlyFollowed, setOnlyFollowed] = useState(false);
  const [query, { data, loading, called }] = useLazyQuery(
    PAGINATED_RANKINGS_QUERY
  );
  const initialPage = so5Ranking?.ranking
    ? Math.floor((so5Ranking.ranking - 1) / PAGE_SIZE)
    : 0;

  if (!called) {
    query({
      variables: {
        slug,
        pageSize: PAGE_SIZE,
        page: initialPage,
        onlyFollowed,
      },
    });
  }

  const { so5RankingsPaginated } = data?.so5.so5Leaderboard || {};
  const { so5Rankings, currentPage, totalCount } = so5RankingsPaginated || {};

  return (
    <Vertical>
      <ToggleFilter
        label={formatMessage({
          id: 'leaderboard.filters.onlyFollowed',
          defaultMessage: 'Only managers I follow',
        })}
        value={onlyFollowed}
        onChange={() => {
          const newOnlyFollowed = !onlyFollowed;
          const newPage = newOnlyFollowed ? 0 : initialPage;
          setPage(newPage);
          setOnlyFollowed(newOnlyFollowed);
          query({
            variables: {
              slug,
              pageSize: PAGE_SIZE,
              page: newPage,
              onlyFollowed: newOnlyFollowed,
            },
          });
        }}
      />
      <Rankings
        style={
          {
            '--nb-rows': so5Rankings?.length || PAGE_SIZE,
          } as CSSProperties
        }
      >
        {loading && <LoadingIndicator fullHeight />}
        {so5Rankings?.map(ranking => (
          <Ranking
            key={ranking.id}
            so5Ranking={ranking}
            isMyRanking={ranking.id === so5Ranking?.id}
            onClick={() =>
              onClickRanking?.(ranking.so5Lineup.so5LeaderboardContender.slug)
            }
          />
        ))}
      </Rankings>
      <Pagination
        currentPage={page || currentPage || 0}
        pages={Math.ceil((totalCount || 0) / PAGE_SIZE)}
        onSelect={p => {
          setPage(p);
          query({
            variables: {
              slug,
              pageSize: PAGE_SIZE,
              page: p,
              onlyFollowed,
            },
          });
        }}
      />
    </Vertical>
  );
};

PaginatedRankings.fragments = {
  so5Ranking: gql`
    fragment PaginatedRankings_so5Ranking on So5Ranking {
      id
      ranking
    }
  ` as TypedDocumentNode<PaginatedRankings_so5Ranking>,
};
